import cx from 'classnames';
import { createContext, useContext } from 'react';
import { ConditionalView } from '../../utils/ConditionalView';
import styles from './Dropzone.module.scss';
import usePresenter from './Dropzone.presenter';
import { DropzoneContextType, DropzoneProps } from './Dropzone.types';

const DropzoneContext = createContext<DropzoneContextType | undefined>(undefined);

const Dropzone = <T extends File | string = File>(props: DropzoneProps<T>) => {
  const {
    status,
    variantProps,
    getRootProps,
    getInputProps,
    empty,
    loading,
    uploaded,
    drag,
    open,
    id,
    isDragActive,
    contextValue,
  } = usePresenter<T>(props);

  return (
    <DropzoneContext.Provider value={contextValue as DropzoneContextType}>
      <div
        {...getRootProps()}
        {...variantProps}
        className={cx(styles.dropzone, props.className)}
      >
        <input {...getInputProps()} id={id} />
        <ConditionalView condition={status === 'Empty'}>
          {empty}
        </ConditionalView>
        <ConditionalView condition={status === 'Loading'}>
          {loading}
        </ConditionalView>
        <ConditionalView condition={status === 'Uploaded'}>
          {uploaded}
        </ConditionalView>
        <ConditionalView condition={drag}>
          <div className={cx(
            styles.dropzoneOverlay,
            {
              [styles.visible]: isDragActive,
            }
          )}>
            {drag}
          </div>
        </ConditionalView>
      </div>
    </DropzoneContext.Provider>
  );
};

export const useDropzoneContext = <T extends File | string = File>() => {
  const context = useContext(DropzoneContext as React.Context<DropzoneContextType<T> | undefined>);
  if (!context) {
    throw new Error('useDropzoneContext must be used within a Dropzone');
  }
  return context;
};

export default Dropzone;
